import { last } from "lodash";

import { isLastMessageSame } from "@/react/components/Chat/ChatWindowV2/helpers";
import type { ChatRoomMessage } from "@/react/types/Chat/ChatRoomMessage";

import type {
  ChatRoomActionsSliceState,
  ChatRoomMessageActionsSliceState,
  ChatRoomsSliceState,
} from "../types";
import {
  getChatRoomMessageLimits,
  isViewingOlderChatRoomMessages,
  shouldTrimChatRoomMessages,
} from "./virtualizedChatRooms";

// Batching configuration
export const BATCH_DELAY_MS = 200;

// Batch queue for each chat room
export const batchQueues = new Map<
  string,
  {
    messages: Array<{
      message: ChatRoomMessage;
    }>;
    timeoutId: NodeJS.Timeout | null;
  }
>();

export const clearBatchQueue = (uuid: string) => {
  const queue = batchQueues.get(uuid);
  if (queue?.timeoutId) {
    clearTimeout(queue.timeoutId);
  }
  batchQueues.delete(uuid);
};

interface ProcessedBatch {
  messages: ChatRoomMessage[];
  wasTrimmed: boolean;
}

const processMessagesBatch = (
  uuid: string,
  currentMessages: ChatRoomMessage[],
  newMessages: Array<{ message: ChatRoomMessage }>,
): ProcessedBatch => {
  let messagesArray = [...currentMessages];

  for (const { message } of newMessages) {
    const lastMessageInArr = last(messagesArray);
    const isNewMessageReached = lastMessageInArr?.id != message.id;

    if (isNewMessageReached && !isLastMessageSame(lastMessageInArr, message)) {
      const indexOfMessageWithSameUUID = messagesArray.findIndex(
        (m: ChatRoomMessage) => m.creation_uuid === message.creation_uuid,
      );
      if (indexOfMessageWithSameUUID > -1) {
        messagesArray[indexOfMessageWithSameUUID] = message;
      } else {
        messagesArray.push(message);
      }
    }
  }

  // Every batch trims the oldest messages once the room is over its limit,
  // counting the new ones, so a burst is trimmed in the same update (see
  // `processBatchedMessages` for when the viewer is reading the oldest ones).
  const wasTrimmed = shouldTrimChatRoomMessages(uuid, messagesArray.length);
  if (wasTrimmed) {
    messagesArray = messagesArray.slice(-getChatRoomMessageLimits(uuid).kept);
  }

  return { messages: messagesArray, wasTrimmed };
};

export const processBatchedMessages = (
  uuid: string,
  set: (fn: (state: ChatRoomsSliceState) => void) => void,
  get: () => ChatRoomsSliceState &
    ChatRoomActionsSliceState &
    ChatRoomMessageActionsSliceState,
) => {
  if (!batchQueues) {
    return;
  }
  const queue = batchQueues.get(uuid);
  if (!queue || queue.messages.length === 0) {
    return;
  }

  const messages = [...queue.messages];
  queue.messages = [];
  queue.timeoutId = null;

  try {
    // Get current state to check conditions
    const currentState = get();
    const chatRoomState = currentState.chatRoomStateMap.get(uuid);

    if (!chatRoomState) {
      return;
    }

    const uiState = chatRoomState.uiState;
    const isInitialPageLoaded = !!uiState?.isInitialPageLoaded;
    const hasNextPage = !!uiState?.hasNextPage;

    if (!isInitialPageLoaded || hasNextPage) {
      return;
    }

    set(state => {
      const chatRoomState = state.chatRoomStateMap.get(uuid);
      if (!chatRoomState) {
        return;
      }

      // At the limit while the viewer reads older messages in a virtualized
      // list: trimming the oldest would pull history out from under them, so
      // stop taking new messages instead. With `hasNextPage` set, later batches
      // are skipped; scrolling down loads the missed messages as next pages,
      // and "Latest messages" jumps straight there. The viewer's own message
      // (an optimistic copy) still goes in: sending scrolls them to the bottom.
      const currentMessages = chatRoomState.chatRoomMessages ?? [];
      const hasOwnMessage = messages.some(({ message }) => message.id < 0);
      if (
        !hasOwnMessage &&
        shouldTrimChatRoomMessages(
          uuid,
          currentMessages.length + messages.length,
        ) &&
        isViewingOlderChatRoomMessages(uuid)
      ) {
        const lastStoredMessage = last(currentMessages);
        if (lastStoredMessage) {
          chatRoomState.uiState.lastMessageId = lastStoredMessage.id;
        }
        chatRoomState.uiState.hasNextPage = true;
        return;
      }

      const { messages: processedMessages, wasTrimmed } = processMessagesBatch(
        uuid,
        chatRoomState?.chatRoomMessages ?? [],
        messages,
      );

      if (wasTrimmed && processedMessages.length > 0) {
        chatRoomState.uiState.firstMessageId = processedMessages[0].id;
        chatRoomState.uiState.hasPrevPage = true;
      }

      chatRoomState.chatRoomMessages = processedMessages;
    });
  } catch (error) {
    console.error("Error processing batched messages:", error);
  }
};
